import { HttpClient } from '../shared/api/HttpClient';
import type { AuthSession, BrowserSession, LoginEvent, SecondFactorChallenge, TotpEnrollment } from './types';

interface Page<T> { items: T[] }

export class AuthApi {
  readonly #http: HttpClient;

  public constructor(http = new HttpClient({ readCsrfToken: readCsrfCookie })) {
    this.#http = http;
  }

  public me(): Promise<AuthSession> { return this.#http.request('/auth/me'); }

  public login(email: string, password: string): Promise<AuthSession | SecondFactorChallenge> {
    return this.#http.request('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  }

  public verifySecondFactor(challengeToken: string, code: string): Promise<AuthSession> {
    return this.#http.request('/auth/totp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeToken, code }),
    });
  }

  public logout(): Promise<void> { return this.#http.request('/auth/logout', { method: 'POST' }); }

  public beginTotpEnrollment(): Promise<TotpEnrollment> {
    return this.#http.request('/auth/totp/enrollment', { method: 'POST' });
  }

  public confirmTotpEnrollment(code: string): Promise<{ recoveryCodes: string[]; sessionsRevoked: true }> {
    return this.#http.request('/auth/totp/enrollment/confirm', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
    });
  }

  public disableTotp(password: string, code: string): Promise<void> {
    return this.#http.request('/auth/totp', {
      method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password, code }),
    });
  }

  public changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.#http.request('/auth/password', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  public async sessions(): Promise<BrowserSession[]> {
    return (await this.#http.request<Page<BrowserSession>>('/auth/sessions')).items;
  }

  public revokeSession(id: string): Promise<void> {
    return this.#http.request(`/auth/sessions/${id}`, { method: 'DELETE' });
  }

  public async loginEvents(): Promise<LoginEvent[]> {
    return (await this.#http.request<Page<LoginEvent>>('/auth/login-events?limit=20')).items;
  }
}

export function isSecondFactorChallenge(value: AuthSession | SecondFactorChallenge): value is SecondFactorChallenge {
  return 'challengeToken' in value;
}

function readCsrfCookie(): string | null {
  const prefix = '__Host-radio_csrf=';
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return value === undefined ? null : decodeURIComponent(value.slice(prefix.length));
}
